import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useQueryClient } from '@tanstack/react-query';
import { Form, Formik } from 'formik';

import { SsoProviderButtons } from '@/components/auth/SsoProviderButtons';
import { Spinner } from '@/components/generic';
import {
  hasInternalProviders,
  hasSecondaryAuthOptions,
  isPasswordLoginEnabled,
  isPasswordLoginPrimary,
  primarySsoProviders,
  secondarySsoProviders
} from '@/configuration/ssoProviders';
import { useAuth } from '@/contexts';
import { PUBLIC_PAGE_ROUTES } from '@/enums';
import {
  handleSsoSignInError,
  isAccountExistsWithDifferentCredentialError,
  isSsoPopupCancelledError
} from '@/helpers';
import { showToast, ToastType } from '@/helpers/toast';
import { PasswordInput } from '@/pages/Login/PasswordInput';
import { setPasswordSchema } from '@/pages/SetPassword/SetPasswordValidation';
import { SentoaLogo } from '@/resources';
import { useGetInvitationOptionsQuery, useSetPasswordMutation } from '@/services';
import { TestIds } from '@/shared/test-ids';
import { SetPasswordForm } from '@/types';

export const SetPassword = () => {
  const navigate = useNavigate();
  const [t] = useTranslation('common');
  const { loginWithSsoProvider } = useAuth();
  const [token] = useState(() => new URLSearchParams(window.location.search).get('token') || '');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submittingProviderId, setSubmittingProviderId] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);
  const needsInvitationOptions = hasSecondaryAuthOptions || hasInternalProviders;
  const {
    data: invitationOptions,
    isFetching,
    isError
  } = useGetInvitationOptionsQuery(token, needsInvitationOptions);
  const showHiddenProviders = needsInvitationOptions
    ? !isError && invitationOptions?.data.showHiddenProviders === true
    : false;
  const showInternalProviders = needsInvitationOptions
    ? !isError && invitationOptions?.data.showInternalProviders === true
    : false;
  const isInvitationLoading = needsInvitationOptions && Boolean(token) && isFetching;

  const showPasswordForm =
    isPasswordLoginEnabled && (isPasswordLoginPrimary || showHiddenProviders);
  const visibleSsoProviders = (
    showHiddenProviders ? [...primarySsoProviders, ...secondarySsoProviders] : primarySsoProviders
  ).filter((provider) => showInternalProviders || !provider.internal);
  const showSso = visibleSsoProviders.length > 0;
  const showDivider = showPasswordForm && showSso;

  const { mutate: setPassword } = useSetPasswordMutation(queryClient, {
    onSuccess: () => {
      setIsSubmitting(false);
      navigate('login');
    },
    onError: () => {
      setIsSubmitting(false);
    }
  });

  const getInitialValues = () => ({
    password: '',
    confirmPassword: ''
  });

  const handleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleShowConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleSubmit = async (values: SetPasswordForm) => {
    setIsSubmitting(true);
    const payload = {
      password: values.password
    };
    setPassword({ token, payload });
  };

  const handleSsoLogin = async (providerId: string) => {
    setSubmittingProviderId(providerId);
    try {
      await loginWithSsoProvider(providerId, { inviteToken: token || undefined });
    } catch (error) {
      if (isSsoPopupCancelledError(error)) {
        return;
      }
      if (isAccountExistsWithDifferentCredentialError(error)) {
        navigate(PUBLIC_PAGE_ROUTES.LOGIN);
        return;
      }
      showToast(ToastType.ERROR, t(handleSsoSignInError(error)));
    } finally {
      setSubmittingProviderId(null);
    }
  };

  return (
    <div className='flex min-h-screen items-center justify-center bg-gray-100'>
      <div className='w-full max-w-md rounded-md bg-white p-8 shadow-md'>
        <div className='flex flex-col gap-6'>
          <div data-testid={TestIds.login.logo} className='flex w-full justify-center'>
            <SentoaLogo className='h-10 w-44' />
          </div>
          <div className='flex justify-center'>
            <div className='h-px w-full bg-blue-powder' />
          </div>

          <div className='flex flex-col gap-4'>
            {isInvitationLoading && (
              <div className='flex justify-center'>
                <Spinner />
              </div>
            )}

            {!isInvitationLoading && showPasswordForm && (
              <Formik
                enableReinitialize
                initialValues={getInitialValues()}
                validateOnBlur={false}
                validationSchema={setPasswordSchema}
                onSubmit={handleSubmit}>
                {({ errors, touched, values }) => (
                  <Form id='confirmPassword'>
                    <PasswordInput
                      id='password'
                      type={showPassword ? 'text' : 'password'}
                      label={t('password')}
                      errorMessage={errors.password}
                      isInvalid={!!errors.password && touched.password}
                      showPassword={handleShowPassword}
                      passwordValue={values.password}
                    />
                    <PasswordInput
                      id='confirmPassword'
                      type={showConfirmPassword ? 'text' : 'password'}
                      label={t('confirmPassword')}
                      errorMessage={errors.confirmPassword}
                      isInvalid={!!errors.confirmPassword && touched.confirmPassword}
                      showPassword={handleShowConfirmPassword}
                      passwordValue={values.confirmPassword}
                    />
                    <div className='mt-4'>
                      <button
                        type='submit'
                        disabled={isSubmitting || !token}
                        className='rounded bg-slate-800 px-6 py-2 font-bold text-white'>
                        {t('setPassword')}
                      </button>
                    </div>
                  </Form>
                )}
              </Formik>
            )}

            {!isInvitationLoading && showDivider && (
              <div className='flex items-center gap-3'>
                <div className='h-px flex-1 bg-blue-powder' />
                <span className='text-sm text-primary-black'>{t('or')}</span>
                <div className='h-px flex-1 bg-blue-powder' />
              </div>
            )}

            {!isInvitationLoading && showSso && (
              <SsoProviderButtons
                providers={visibleSsoProviders}
                showDivider={false}
                showLabels={visibleSsoProviders.length === 1}
                onProviderClick={handleSsoLogin}
                submittingProviderId={submittingProviderId}
                disabled={!token}
                buttonTestIdPrefix={TestIds.login.ssoButton}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
